/**
 * 人物 — the people the Analects is actually about.
 *
 * A student who can tell 顏回 from 子路 from 子貢 reads the book very
 * differently from one who sees 541 anonymous sayings: the same question gets
 * different answers because the questioner is different. Each figure carries
 * the passages that define them and the one trait that explains those passages.
 */
export type FigureId =
  | "kongzi"
  | "yanhui"
  | "zilu"
  | "zigong"
  | "zengzi"
  | "zixia"
  | "ziyou"
  | "ranyou"
  | "zaiwo"
  | "zizhang"
  | "minziqian"
  | "zhonggong"
  | "fanchi"
  | "guanzhong"
  | "boyi";

export interface Figure {
  id: FigureId;
  name: string;
  /** 字 / 別稱, so a student recognises 由 = 仲由 = 季路 = 子路. */
  aka: string;
  /** disciple, or a historical figure the book judges. */
  kind: "master" | "disciple" | "historical";
  /** The single trait that makes their passages make sense. */
  trait: string;
  /** What Confucius characteristically does with this person. */
  note: string;
  keyPassages: number[];
}

export const FIGURES: Figure[] = [
  {
    id: "kongzi",
    name: "孔子",
    aka: "仲尼、夫子、丘",
    kind: "master",
    trait: "述而不作，學而不厭，知其不可而為之。",
    note: "自述多是「我未能」「吾猶人也」，最高的自許只到「好學」與「誨人不倦」。",
    keyPassages: [20, 151, 152, 170, 174, 184, 411, 414],
  },
  {
    id: "yanhui",
    name: "顏回",
    aka: "顏淵、回",
    kind: "disciple",
    trait: "德行第一：安貧、不遷怒、不貳過，三月不違仁。",
    note: "孔子唯一無保留稱許的弟子；他早死時孔子說「天喪予」，並拒絕厚葬以守禮。",
    keyPassages: [25, 101, 123, 127, 131, 220, 229, 230, 274, 277, 279, 280, 281, 293, 323, 431],
  },
  {
    id: "zilu",
    name: "子路",
    aka: "仲由、由、季路",
    kind: "disciple",
    trait: "勇而直率，聞義即行，失之於粗率好勇。",
    note: "孔子常抑其勇：同一句「聞斯行諸」，子路要他退一步，冉有要他進一步。",
    keyPassages: [33, 99, 100, 106, 118, 128, 148, 161, 169, 185, 221, 236, 282, 285, 292, 294, 295, 296, 334, 347, 349, 388, 392, 398, 418, 422, 481, 483, 499, 509],
  },
  {
    id: "zigong",
    name: "子貢",
    aka: "端木賜、賜",
    kind: "disciple",
    trait: "言語第一：善辯、善貨殖，長於比較與評論人物。",
    note: "孔子常提醒他少評人、多求己；「賜也賢乎哉？夫我則不暇」。",
    keyPassages: [10, 15, 29, 57, 96, 101, 104, 105, 107, 150, 215, 222, 286, 289, 329, 330, 345, 366, 370, 393, 405, 411, 423, 430, 444, 495, 500, 533, 534, 536, 537, 538],
  },
  {
    id: "zengzi",
    name: "曾參",
    aka: "曾子、參",
    kind: "disciple",
    trait: "魯鈍而篤實，以「日三省」與「任重道遠」立身。",
    note: "受「吾道一以貫之」之教，並以忠恕解之；臨終仍請人啟手足以驗守身。",
    keyPassages: [4, 9, 81, 191, 192, 193, 194, 195, 346, 402, 529, 530, 531, 532],
  },
  {
    id: "zixia",
    name: "子夏",
    aka: "卜商、商",
    kind: "disciple",
    trait: "文學第一，篤學守小節，失之於拘謹「不及」。",
    note: "《子張》篇多存其語；孔子誡之「女為君子儒，無為小人儒」。",
    keyPassages: [7, 24, 48, 133, 286, 327, 363, 516, 517, 518, 519, 520, 521, 522, 523, 524, 526],
  },
  {
    id: "ziyou",
    name: "子游",
    aka: "言偃",
    kind: "disciple",
    trait: "文學科，重禮樂教化的本末之辨。",
    note: "在武城以弦歌治邑，被孔子「割雞焉用牛刀」戲之而後自承其言為是。",
    keyPassages: [23, 92, 134, 480, 525, 527, 528],
  },
  {
    id: "ranyou",
    name: "冉有",
    aka: "冉求、求",
    kind: "disciple",
    trait: "政事科，多才而退縮，易屈從權勢。",
    note: "為季氏聚斂，孔子怒斥「非吾徒也，小子鳴鼓而攻之可也」。",
    keyPassages: [46, 124, 132, 165, 287, 292, 294, 296, 355, 360, 463],
  },
  {
    id: "zaiwo",
    name: "宰我",
    aka: "宰予、予",
    kind: "disciple",
    trait: "言語科，好設難問，屢遭孔子責備。",
    note: "晝寢被斥「朽木不可雕」；又以三年之喪為久，孔子判其「不仁」。",
    keyPassages: [61, 102, 146, 273, 497],
  },
  {
    id: "zizhang",
    name: "子張",
    aka: "顓孫師、師",
    kind: "disciple",
    trait: "才高意廣而失之於「過」，重外顯的達與聞。",
    note: "問「達」被孔子點破：他說的其實是「聞」不是「達」。",
    keyPassages: [34, 39, 111, 286, 290, 328, 332, 336, 342, 416, 426, 482, 514, 515, 516, 540],
  },
  {
    id: "minziqian",
    name: "閔子騫",
    aka: "閔損",
    kind: "disciple",
    trait: "德行科，以孝著稱，辭季氏之聘以守身。",
    note: "「孝哉閔子騫！人不間於其父母昆弟之言」——連外人都挑不出話說。",
    keyPassages: [129, 275, 283, 284],
  },
  {
    id: "zhonggong",
    name: "仲弓",
    aka: "冉雍、雍",
    kind: "disciple",
    trait: "德行科，寬簡而莊敬，孔子許其「可使南面」。",
    note: "問仁得「出門如見大賓，使民如承大祭」，是仁的政事版本。",
    keyPassages: [97, 121, 122, 126, 324, 348],
  },
  {
    id: "fanchi",
    name: "樊遲",
    aka: "樊須",
    kind: "disciple",
    trait: "質樸好問而遲鈍，屢問仁與知。",
    note: "請學稼被斥為小人之志；但也正因他一再追問，留下「仁者愛人，知者知人」。",
    keyPassages: [21, 142, 343, 344, 350, 365],
  },
  {
    id: "guanzhong",
    name: "管仲",
    aka: "管夷吾",
    kind: "historical",
    trait: "齊桓公之相，功業極大而不知禮、器量小。",
    note: "孔子對他兩面評價：譏其「器小」「不知禮」，卻許其「如其仁」——功在天下。",
    keyPassages: [62, 385, 392, 393],
  },
  {
    id: "boyi",
    name: "伯夷叔齊",
    aka: "孤竹二子",
    kind: "historical",
    trait: "求仁得仁的極端典型：不食周粟，餓於首陽。",
    note: "孔子以之為「不降其志，不辱其身」，並藉子貢之問表明自己不為衛君。",
    keyPassages: [115, 165, 474, 510],
  },
];

export const FIGURE_MAP = new Map(FIGURES.map((f) => [f.id, f]));

export function figureById(id: FigureId): Figure {
  const figure = FIGURE_MAP.get(id);
  if (!figure) throw new Error(`unknown figure: ${id}`);
  return figure;
}
